import {IUserInfo} from 'utils/Atoms';
import {
  IContractorList,
  IPermissionState,
  ITongPlaceState,
  ResponseSearchBackOfficeContractor,
  ResponseSearchBackOfficeTongPlace,
  ResSearchWorker,
  TongPlaceList,
} from 'interfaces/interface';
import {
  searchBackOfficeContractor,
  searchBackOfficePartnerCompany,
  searchBackOfficeSiteAttendeeCounts,
  searchBackOfficeTongPlace,
  searchWorker,
} from 'function/api';
import {
  ISiteStatusCounts,
  createEmptySiteStatusCounts,
  getSiteStatusKey,
} from 'utils/siteStatus';

/**
 * 한 페이지당 조회 건수 (전수 조회 시 사용)
 * N+1을 피하기 위해 큰 페이지 크기로 페이지네이션 루프를 돌립니다.
 */
const PAGE_SIZE = 500;

/**
 * 통계 서비스 공통 파라미터
 * 페이지 단위 로딩 토글 대신 화면에서 단일 로딩 상태를 제어하므로
 * setIsLoading은 받지 않고 setErrorModal만 받습니다.
 */
interface IStatisticsParams {
  userInfo: IUserInfo | null;
  setErrorModal: React.Dispatch<React.SetStateAction<{}>>;
}

/**
 * 개별 현장의 통계 표시 정보
 */
export interface IContractorStatSite {
  siteId: string;
  siteName: string;
  rawState: ITongPlaceState | null;
  rawAllowState: IPermissionState | null;
  /** 상태는 현재(최신) 상태 기준입니다. (월별 과거 스냅샷 아님) */
  statusKey: ReturnType<typeof getSiteStatusKey>;
  /** 현장 생성일 (ISO) */
  createdAt?: string;
  /** 운영(공사) 시작일 */
  constructionStartDate?: string;
  /** 운영(공사) 종료일 */
  constructionEndDate?: string;
}

/**
 * 시공사 단위 통계 정보
 */
export interface IContractorStat {
  contractorId: string;
  contractorName: string;
  /** 시공사 정보가 없는(이름 매칭 실패) 현장/협력사를 모은 가상 그룹 여부 */
  isUnassigned: boolean;
  siteCount: number;
  siteStatusCounts: ISiteStatusCounts;
  /** 선택 기간(월별)에 운영 중이던 현장 수 (운영기간 overlap 기준, 전체 모드면 0) */
  operatingSiteCount: number;
  partnerCompanyCount: number;
  partnerCompanyNames: string[];
  /** 기간 내 생성된(신규) 현장 목록 */
  sites: IContractorStatSite[];
  /** 선택 기간(월별)에 운영 중이던 현장 목록 (운영기간 overlap 기준) */
  operatingSites: IContractorStatSite[];
}

export type ContractorSortBy =
  | 'contractorName'
  | 'siteCount'
  | 'operatingSiteCount'
  | 'operating'
  | 'paused'
  | 'pendingApproval'
  | 'closed'
  | 'partnerCompanyCount';

export const sortContractorStatistics = (
  contractors: IContractorStat[],
  sortBy: ContractorSortBy,
  sortOrder: 'asc' | 'desc',
) => {
  const collator = new Intl.Collator('ko-KR', {
    numeric: true,
    sensitivity: 'base',
  });
  const numberValue = (contractor: IContractorStat) => {
    switch (sortBy) {
      case 'siteCount':
      case 'operatingSiteCount':
      case 'partnerCompanyCount':
        return contractor[sortBy];
      case 'operating':
      case 'paused':
      case 'pendingApproval':
      case 'closed':
        return contractor.siteStatusCounts[sortBy];
      default:
        return 0;
    }
  };

  return [...contractors].sort((a, b) => {
    if (Boolean(a.isUnassigned) !== Boolean(b.isUnassigned)) {
      return a.isUnassigned ? 1 : -1;
    }
    const compared =
      sortBy === 'contractorName'
        ? collator.compare(a.contractorName, b.contractorName)
        : numberValue(a) - numberValue(b);
    return (
      (sortOrder === 'asc' ? compared : -compared) ||
      collator.compare(a.contractorName, b.contractorName)
    );
  });
};

/**
 * 시공사 통계 전체 응답
 *
 * total 계열은 기간과 무관한 누적 전체 값이고,
 * new/period 계열은 선택한 기간(월별) 기준 값입니다. (전체 모드면 total과 동일)
 */
export interface IContractorStatistics {
  totalContractorCount: number;
  /** 해당 기간에 신규 등록된 시공사 수 (전체 모드면 total과 동일) */
  newContractorCount: number;
  /** 누적 전체 현장 수 (기간 무관) */
  totalSiteCount: number;
  /** 해당 기간에 신규 생성된 현장 수 (전체 모드면 total과 동일) */
  periodSiteCount: number;
  /** 기간 범위 현장의 상태별 카운트 (테이블/상세와 동일 스코프) */
  siteStatusCounts: ISiteStatusCounts;
  totalPartnerCompanyCount: number;
  /** 해당 기간에 신규 등록된 협력사 수 (전체 모드면 total과 동일) */
  newPartnerCompanyCount: number;
  contractors: IContractorStat[];
}

/**
 * 현장별 협력사 (지연 조회 결과)
 */
export interface ISitePartnerCompany {
  partnerCompanyId: string;
  partnerCompanyName: string;
}

/** 현장별 출근 집계 표시 상태 */
export type SiteAttendanceState = 'loading' | 'unavailable' | 'error' | number;

/**
 * 현장별 출근 집계 상태를 화면 표시 문구로 변환합니다.
 * API가 아직 배포되지 않았거나 404/미연동이면 "미연동"으로 표시해
 * 0명/오류와 구분합니다.
 */
export const getSiteAttendanceLabel = (
  attendance?: SiteAttendanceState,
): string => {
  if (attendance === undefined || attendance === 'loading') {
    return '출근 …';
  }
  if (attendance === 'unavailable') {
    return '출근 집계 미연동';
  }
  if (attendance === 'error') {
    return '출근 집계 오류';
  }
  return attendance > 0 ? `출근 O · ${attendance}명` : '출근 X';
};

/**
 * 집계 전 원본 데이터 (전체 시공사/현장/협력사)
 * 한 번만 조회한 뒤, 기간 필터에 따라 클라이언트에서 재집계합니다.
 */
export interface IContractorStatisticsRaw {
  contractors: IContractorList[];
  sites: TongPlaceList[];
  partners: any[];
}

/**
 * 기간 필터 (epoch ms 구간). null이면 전체.
 * 현장(createdAt) 기준으로 필터링합니다.
 */
export interface IStatPeriod {
  start: number;
  end: number;
}

/** 이름 매칭 실패 항목을 모으는 가상 그룹 키 */
const UNASSIGNED_KEY = '__UNASSIGNED__';

/** setIsLoading을 요구하는 기존 API 함수에 넘길 no-op */
const noopSetLoading: React.Dispatch<React.SetStateAction<boolean>> = () =>
  undefined;

/** 전역 에러 모달을 띄우지 않기 위한 no-op (실패해도 화면 폴백으로 처리) */
const noopSetErrorModal: React.Dispatch<React.SetStateAction<{}>> = () =>
  undefined;

/**
 * 이름 기반 조인 키를 정규화합니다. (좌우 공백 제거)
 */
const normalizeName = (name?: string | null): string => (name ?? '').trim();

/**
 * 협력사 uuid가 없는 legacy/API 응답도 누락하지 않도록 통계용 dedupe key를 만듭니다.
 * uuid가 있으면 uuid를 우선하고, 없으면 시공사 식별자 + 협력사명 조합으로 fallback 합니다.
 */
export const getPartnerDedupeKey = (partner: any): string => {
  const partnerUuid = partner?.uuid;
  if (partnerUuid) {
    return `uuid:${partnerUuid}`;
  }
  const partnerName = normalizeName(partner?.name);
  if (!partnerName) {
    return '';
  }
  const contractorKey =
    partner?.contractor?.uuid || normalizeName(partner?.contractor?.name);
  return `fallback:${contractorKey || 'unknown'}:${partnerName}`;
};

/**
 * 페이지네이션 API를 전수 조회합니다.
 * 1페이지를 먼저 가져와 totalCount로 전체 페이지 수를 계산한 뒤,
 * 나머지 페이지를 병렬로 가져옵니다. (현장당 반복 호출 = N+1 아님)
 *
 * @param fetchPage - (page, count) => 응답 또는 false
 * @param pick - 응답에서 {items, totalCount} 추출
 * @returns 전체 아이템 배열 (실패 시 빈 배열)
 */
async function fetchAllPages<R, Item>(
  fetchPage: (page: number, count: number) => Promise<R | false>,
  pick: (res: R) => {items: Item[]; totalCount: number},
): Promise<Item[] | false> {
  const first = await fetchPage(1, PAGE_SIZE);
  if (!first) {
    return false;
  }
  const {items, totalCount} = pick(first);
  const all: Item[] = [...items];

  const totalPages = Math.ceil((totalCount || items.length) / PAGE_SIZE);
  if (totalPages <= 1) {
    return all;
  }

  const restPages = Array.from({length: totalPages - 1}, (_, i) => i + 2);
  const restResults = await Promise.all(
    restPages.map(page => fetchPage(page, PAGE_SIZE)),
  );
  if (restResults.some(res => res === false)) {
    return false;
  }
  restResults.forEach(res => {
    all.push(...pick(res as R).items);
  });

  return all;
}

/**
 * 빈 시공사 통계 그룹을 생성합니다.
 */
const createStatGroup = (
  contractorId: string,
  contractorName: string,
  isUnassigned: boolean,
): IContractorStat => ({
  contractorId,
  contractorName,
  isUnassigned,
  siteCount: 0,
  siteStatusCounts: createEmptySiteStatusCounts(),
  operatingSiteCount: 0,
  partnerCompanyCount: 0,
  partnerCompanyNames: [],
  sites: [],
  operatingSites: [],
});

/**
 * 현장 운영기간(constructionStart~End)이 기간 구간과 겹치는지 검사합니다.
 * (해당 월에 '운영 중이던' 현장 판정 — 생성일이 아니라 운영기간 기준)
 */
const isOperatingInPeriod = (
  constructionStartDate: string | undefined | null,
  constructionEndDate: string | undefined | null,
  period: IStatPeriod | null,
): boolean => {
  if (!period) {
    return false;
  }
  const start = constructionStartDate
    ? new Date(constructionStartDate).getTime()
    : NaN;
  const end = constructionEndDate
    ? new Date(constructionEndDate).getTime()
    : NaN;
  if (Number.isNaN(start) || Number.isNaN(end)) {
    return false;
  }
  return start < period.end && end >= period.start;
};

/**
 * 전체 시공사/현장/협력사 원본 데이터를 전수 조회합니다.
 *
 * list API가 날짜 파라미터를 지원하지 않으므로, 기간 필터·페이지네이션은
 * 받아온 원본에 대해 클라이언트에서 처리합니다. 이 함수는 한 번만 호출하고,
 * 이후 기간 변경 시에는 aggregateContractorStatistics로 재집계합니다.
 *
 * @param params - {userInfo, setErrorModal}
 * @returns 원본 데이터 또는 false
 */
export const fetchContractorStatisticsRaw = async ({
  userInfo,
  setErrorModal,
}: IStatisticsParams): Promise<IContractorStatisticsRaw | false> => {
  if (!userInfo?.accessToken) {
    return false;
  }

  try {
    const [contractors, sites, partners] = await Promise.all([
      fetchAllPages<ResponseSearchBackOfficeContractor, IContractorList>(
        (page, count) =>
          searchBackOfficeContractor({
            userInfo,
            setIsLoading: noopSetLoading,
            setErrorModal,
            page,
            count,
          }),
        res => ({
          items: res.contractorList ?? [],
          totalCount: res.totalCount ?? 0,
        }),
      ),
      fetchAllPages<ResponseSearchBackOfficeTongPlace, TongPlaceList>(
        (page, count) =>
          searchBackOfficeTongPlace({
            userInfo,
            setIsLoading: noopSetLoading,
            setErrorModal,
            page,
            count,
            // 숨김 처리한 현장은 통계에서 제외한다.
            // searchBackOfficeTongPlace 의 기본값이 'ALL'(= 서버 withDeleted: true)이라
            // 넘기지 않으면 숨긴 현장이 그대로 집계에 남는다.
            hiddenState: 'VISIBLE',
          }),
        res => ({
          items: res.tongPlaceList ?? [],
          totalCount: res.totalCount ?? 0,
        }),
      ),
      fetchAllPages<any, any>(
        (page, count) =>
          searchBackOfficePartnerCompany({
            userInfo,
            setIsLoading: noopSetLoading,
            setErrorModal,
            page,
            count,
          }),
        res => ({
          items: res?.partnerCompanies ?? [],
          totalCount: res?.totalCount ?? 0,
        }),
      ),
    ]);

    if (contractors === false || sites === false || partners === false) {
      return false;
    }

    return {contractors, sites, partners};
  } catch (error: any) {
    setErrorModal({
      onOff: true,
      status: error?.response?.status,
      title: error?.response?.title,
      detail: error?.response?.detail,
    });
    return false;
  }
};

/**
 * createdAt이 기간 구간에 포함되는지 검사합니다. (현장/시공사/협력사 공통)
 * period가 null이면 항상 true(전체).
 */
export const isCreatedInPeriod = (
  createdAt: string | undefined | null,
  period: IStatPeriod | null,
): boolean => {
  if (!period) {
    return true;
  }
  if (!createdAt) {
    return false;
  }
  const t = new Date(createdAt).getTime();
  if (Number.isNaN(t)) {
    return false;
  }
  return t >= period.start && t < period.end;
};

/** createdAt 내림차순(최신순) 정렬 비교자 */
const byCreatedAtDesc = (
  a: {createdAt?: string},
  b: {createdAt?: string},
): number =>
  new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime();

/**
 * 원본 데이터를 시공사 통계로 집계합니다. (순수 함수)
 *
 * 데이터 관계 (실제 모델 기준):
 * - 시공사 → 현장 : 현장(TongPlaceList)의 contractorName(문자열)으로 연결
 * - 시공사 → 협력사: 협력사의 contractor.name(문자열)으로 직접 연결
 *   (현장↔협력사 직접 연결은 list API에 없으며, 현장별 협력사는
 *    getSitePartnerCompanies로 지연 조회한다.)
 *
 * 기간(period)은 현장 createdAt 기준으로 적용됩니다.
 * (전체 시공사 수·협력사 수는 등록 전체 기준으로 유지)
 * 이름이 매칭되지 않는 현장/협력사는 "(미지정 시공사)" 그룹으로 모읍니다.
 *
 * @param raw - 원본 데이터
 * @param period - 기간 구간(null이면 전체)
 * @returns 집계된 통계
 */
export const aggregateContractorStatistics = (
  raw: IContractorStatisticsRaw,
  period: IStatPeriod | null = null,
): IContractorStatistics => {
  const {contractors, partners} = raw;
  // 기간 필터(현장 createdAt) 후 최신 생성순으로 정렬 → 상세의 현장 목록 순서 보장
  const sites = raw.sites
    .filter(s => isCreatedInPeriod(s.createdAt, period))
    .sort(byCreatedAtDesc);

  // 그룹은 시공사 uuid를 1차 키로 사용하고, 이름은 폴백 매칭용으로만 둡니다.
  // (현장/협력사 응답이 contractorUUID를 제공하면 정확히 매칭, 없으면 이름으로 폴백)
  const groups = new Map<string, IContractorStat>();
  const groupByName = new Map<string, IContractorStat>();

  // 1) 시공사 목록으로 그룹 시드
  contractors.forEach(c => {
    if (!c.uuid || groups.has(c.uuid)) {
      return;
    }
    const group = createStatGroup(c.uuid, c.name ?? '', false);
    groups.set(c.uuid, group);
    const nameKey = normalizeName(c.name);
    if (nameKey && !groupByName.has(nameKey)) {
      groupByName.set(nameKey, group);
    }
  });

  const getOrCreateUnassigned = (): IContractorStat => {
    let g = groups.get(UNASSIGNED_KEY);
    if (!g) {
      g = createStatGroup('', '', true);
      groups.set(UNASSIGNED_KEY, g);
    }
    return g;
  };

  /** uuid 우선, 없으면 이름으로 폴백하여 그룹을 찾습니다. */
  const matchGroup = (
    uuid?: string | null,
    name?: string | null,
  ): IContractorStat => {
    if (uuid && groups.has(uuid)) {
      return groups.get(uuid) as IContractorStat;
    }
    const nameKey = normalizeName(name);
    if (nameKey && groupByName.has(nameKey)) {
      return groupByName.get(nameKey) as IContractorStat;
    }
    return getOrCreateUnassigned();
  };

  // 2) 현장을 시공사로 그룹핑 (contractorUUID 우선)
  const totalSiteStatusCounts = createEmptySiteStatusCounts();
  sites.forEach(site => {
    const group = matchGroup(site.contractorUUID, site.contractorName);

    const statusKey = getSiteStatusKey(site.state, site.allowState);
    group.sites.push({
      siteId: site.uuid,
      siteName: site.name ?? '',
      rawState: (site.state as ITongPlaceState) ?? null,
      rawAllowState: (site.allowState as IPermissionState) ?? null,
      statusKey,
      createdAt: site.createdAt,
      constructionStartDate: site.constructionStartDate,
      constructionEndDate: site.constructionEndDate,
    });
    group.siteStatusCounts[statusKey] += 1;
    group.siteCount += 1;
    totalSiteStatusCounts[statusKey] += 1;
  });

  // 3) 협력사를 시공사로 그룹핑 (uuid 우선, 없으면 시공사+협력사명 기준 중복 제거)
  const partnerSeenByGroup = new Map<IContractorStat, Set<string>>();
  const allPartnerKeys = new Set<string>();
  partners.forEach(partner => {
    const dedupeKey = getPartnerDedupeKey(partner);
    if (!dedupeKey) {
      return;
    }
    allPartnerKeys.add(dedupeKey);
    const group = matchGroup(
      partner?.contractor?.uuid,
      partner?.contractor?.name,
    );

    let seen = partnerSeenByGroup.get(group);
    if (!seen) {
      seen = new Set<string>();
      partnerSeenByGroup.set(group, seen);
    }
    if (seen.has(dedupeKey)) {
      return;
    }
    seen.add(dedupeKey);
    group.partnerCompanyCount += 1;
    group.partnerCompanyNames.push(partner?.name ?? '');
  });

  // 4) 시공사별 '그 달 운영 현장 수' (전체 현장 대상 운영기간 overlap, 월별 모드에서만)
  if (period) {
    raw.sites.forEach(site => {
      if (
        !isOperatingInPeriod(
          site.constructionStartDate,
          site.constructionEndDate,
          period,
        )
      ) {
        return;
      }
      const group = matchGroup(site.contractorUUID, site.contractorName);
      group.operatingSiteCount += 1;
      group.operatingSites.push({
        siteId: site.uuid,
        siteName: site.name ?? '',
        rawState: (site.state as ITongPlaceState) ?? null,
        rawAllowState: (site.allowState as IPermissionState) ?? null,
        statusKey: getSiteStatusKey(site.state, site.allowState),
        createdAt: site.createdAt,
        constructionStartDate: site.constructionStartDate,
        constructionEndDate: site.constructionEndDate,
      });
    });
  }

  // 정렬: 실제 시공사 먼저(현장 많은 순), 미지정 그룹은 항상 마지막
  const contractorStats = Array.from(groups.values()).sort((a, b) => {
    if (a.isUnassigned !== b.isUnassigned) {
      return a.isUnassigned ? 1 : -1;
    }
    return b.siteCount - a.siteCount;
  });

  // 기간(월별) 기준 신규 시공사/협력사 수 (전체 모드면 누적 전체와 동일)
  const newContractorCount = contractors.filter(c =>
    isCreatedInPeriod(c.createdAt, period),
  ).length;
  const newPartnerKeys = new Set<string>();
  partners.forEach(partner => {
    if (isCreatedInPeriod(partner?.createdAt, period)) {
      const dedupeKey = getPartnerDedupeKey(partner);
      if (dedupeKey) {
        newPartnerKeys.add(dedupeKey);
      }
    }
  });

  return {
    totalContractorCount: contractors.length,
    newContractorCount,
    totalSiteCount: raw.sites.length,
    periodSiteCount: sites.length,
    siteStatusCounts: totalSiteStatusCounts,
    totalPartnerCompanyCount: allPartnerKeys.size,
    newPartnerCompanyCount: newPartnerKeys.size,
    contractors: contractorStats,
  };
};

/**
 * 특정 현장에 연결된 협력사를 지연 조회합니다.
 *
 * 백오피스 API에는 "현장별 협력사" 전용 엔드포인트가 없으므로,
 * 현장 근로자 권한 프로필(searchWorker)에서 partnerCompanyUUID를 가진
 * 근로자들의 소속 협력사를 distinct 하게 도출합니다.
 * 즉 "현장에 등록된 근로자 기준 협력사"입니다.
 *
 * @param params - {userInfo, setErrorModal, tongPlaceUUID}
 * @returns 협력사 목록 또는 false
 */
export const getSitePartnerCompanies = async ({
  userInfo,
  setErrorModal,
  tongPlaceUUID,
}: IStatisticsParams & {tongPlaceUUID: string}): Promise<
  ISitePartnerCompany[] | false
> => {
  if (!userInfo?.accessToken || !tongPlaceUUID) {
    return false;
  }

  const workers = await fetchAllPages<
    ResSearchWorker,
    ResSearchWorker['tongPlacePermissionProfile'][number]
  >(
    (page, count) =>
      searchWorker({
        userInfo,
        setIsLoading: noopSetLoading,
        setErrorModal,
        tongPlaceUUID,
        page,
        count,
      }),
    res => ({
      items: res.tongPlacePermissionProfile ?? [],
      totalCount: res.count ?? 0,
    }),
  );
  if (workers === false) {
    return false;
  }

  const seen = new Set<string>();
  const result: ISitePartnerCompany[] = [];
  workers.forEach(worker => {
    const id = worker.partnerCompanyUUID;
    if (!id || seen.has(id)) {
      return;
    }
    seen.add(id);
    result.push({
      partnerCompanyId: id,
      partnerCompanyName: worker.affiliation ?? '',
    });
  });

  return result;
};

/**
 * 여러 현장의 기간 내 출근 인원(distinct) 수를 한 번에 조회합니다.
 *
 * 현장 출근 단일 원천(TongPlaceAttendance)에서 집계하므로, 시공사/협력사 등
 * 어느 경로로 찍힌 출근이든 해당 현장에 기록되면 포함되고, 같은 사람은 1명으로
 * 중복 제거됩니다. 기간(period)은 현장별 출근 표시에만 쓰이며 전체 모드에서는 호출하지 않습니다.
 *
 * @param params - {userInfo, setErrorModal, tongPlaceUUIDList, period}
 * @returns {현장UUID: 인원수} 맵 또는 false
 */
export const getSiteAttendeeCounts = async ({
  userInfo,
  tongPlaceUUIDList,
  period,
}: IStatisticsParams & {
  tongPlaceUUIDList: string[];
  period: IStatPeriod;
}): Promise<{[tongPlaceUUID: string]: number} | false> => {
  if (!userInfo?.accessToken || !tongPlaceUUIDList.length) {
    return false;
  }
  const chunks = Array.from(
    {length: Math.ceil(tongPlaceUUIDList.length / PAGE_SIZE)},
    (_, i) => tongPlaceUUIDList.slice(i * PAGE_SIZE, (i + 1) * PAGE_SIZE),
  );
  // 실패해도 전역 에러 모달을 띄우지 않고 false 반환 → 화면에서 '출근 집계 미연동'으로 폴백
  const results = await Promise.all(
    chunks.map(chunk =>
      searchBackOfficeSiteAttendeeCounts({
        userInfo,
        setIsLoading: noopSetLoading,
        setErrorModal: noopSetErrorModal,
        tongPlaceUUIDList: chunk,
        startDate: new Date(period.start).toISOString(),
        endDate: new Date(period.end).toISOString(),
      }),
    ),
  );
  return results.some(result => result === false)
    ? false
    : Object.assign({}, ...results);
};
